import {
  Box,
  Button,
  Card,
  Divider,
  FormControlSizes,
  HStack,
  IconName,
  InlineFormattedNumber,
  LoaderTalos,
  Text,
  VStack,
  formattedDateForSubscription,
  usePortal,
  type ReconMismatch,
} from '@talos/kyoko';
import { useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { LedgerEventsScatterChart } from './LedgerEventsScatterChart';
import { ReconDetailsInteractionsProvider, useReconDetailsInteractions } from './ReconDetailsInteractionsProvider';
import { ReconHeader } from './ReconHeader';
import { GROUP_BY_EVENTS_TOGGLE_PORTAL_ID, ReconLedgerEventsBlotter } from './ReconLedgerEventsBlotter';
import { useMarketAccountLedgerEvents } from './useMarketAccountLedgerEvents';
import { useReconMismatches } from './useReconMismatches';

export interface ReconMismatchesDetailsProps {
  mismatchIDs: string[];
}

export const ReconMismatchesDetails = ({ mismatchIDs }: ReconMismatchesDetailsProps) => {
  const reconMismatches = useReconMismatches(mismatchIDs, 'ReconMismatchesDetails');

  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const selectedReconMismatch = useMemo(
    () => (reconMismatches ? reconMismatches.at(selectedIndex) : undefined),
    [reconMismatches, selectedIndex]
  );

  // When the provided mismatchIDs change, we always want to reset the selectedIndex.
  useEffect(() => {
    setSelectedIndex(0);
  }, [mismatchIDs]);

  if (!reconMismatches) {
    return <LoaderTalos />;
  }

  if (!selectedReconMismatch) {
    // done loading but we couldnt find a recon mismatch
    return <Text>No Recon Mismatch found</Text>;
  }

  return (
    <ReconDetailsInteractionsProvider>
      <VStack flex="1" overflow="hidden" h="100%">
        <ReconHeader
          reconMismatches={reconMismatches}
          selectedReconMismatch={selectedReconMismatch}
          /* These props should be lifted into a context if we ever add any more. For now its fine, but any more and we get rid of it */
          selectedIndex={selectedIndex}
          onSelectedIndexChange={setSelectedIndex}
        />

        <Box flex="1" w="100%" h="100%" p="spacing12" overflow="auto" background="backgroundContent">
          <Card flex="1" h="100%" p="0" minHeight="450px">
            <ReconMismatchesDetailsBody reconMismatch={selectedReconMismatch} key={selectedReconMismatch.ID} />
          </Card>
        </Box>
      </VStack>
    </ReconDetailsInteractionsProvider>
  );
};

// Brought into its own component so we can conditionally create the filter and subscription properly
const ReconMismatchesDetailsBody = ({ reconMismatch }: { reconMismatch: ReconMismatch }) => {
  const theme = useTheme();

  const { setPortalRef } = usePortal(GROUP_BY_EVENTS_TOGGLE_PORTAL_ID);

  const filter = useMemo(
    () => ({
      MarketAccounts: [reconMismatch.MarketAccount],
      Symbols: [reconMismatch.Asset],
      StartDate: formattedDateForSubscription(reconMismatch.bufferedStartTime),
      EndDate: formattedDateForSubscription(reconMismatch.bufferedEndTime),
    }),
    [reconMismatch]
  );

  const { data: ledgerEventsObs, error: errorObservable } = useMarketAccountLedgerEvents({
    filter,
    tag: 'ReconMismatchesDetailsBody',
  });
  const { zoomOutChart } = useReconDetailsInteractions();

  return (
    <>
      <HStack justifyContent="space-between" borderBottom={`1px solid ${theme.backgroundDivider}`} px="spacing16">
        <Text color="colorTextImportant" py="spacing12">
          Internal Events Ledger
        </Text>
        <HStack gap="spacing8">
          {/* Portalized the group by event toggle into the card header */}
          <Box ref={setPortalRef} />
          <Divider orientation="vertical" />
          <Button startIcon={IconName.ZoomReset} size={FormControlSizes.Small} onClick={zoomOutChart}>
            Reset chart
          </Button>
        </HStack>
      </HStack>
      <VStack flex="1">
        <VStack h="120px" w="100%" borderBottom={`1px solid ${theme.backgroundDivider}`} p="spacing12" gap="spacing8">
          <HStack w="100%" justifyContent="space-between" fontSize="fontSizeSmall">
            <Text>
              Starting Balance:{' '}
              <InlineFormattedNumber
                number={reconMismatch.StartAmount}
                currency={reconMismatch.Asset}
                align="left"
                highlightColor="colorTextDefault"
                truncate={false}
              />
            </Text>
            <Text>
              Ending Balance:{' '}
              <InlineFormattedNumber
                number={reconMismatch.ResultAmount}
                currency={reconMismatch.Asset}
                align="right"
                highlightColor="colorTextDefault"
                truncate={false}
              />
            </Text>
          </HStack>
          <Box w="100%" flex="1">
            <LedgerEventsScatterChart reconMismatch={reconMismatch} ledgerEventsObs={ledgerEventsObs} />
          </Box>
        </VStack>
        <Box flex="1" w="100%" p="spacing12">
          <ReconLedgerEventsBlotter ledgerEventsObs={ledgerEventsObs} errorObservable={errorObservable} />
        </Box>
      </VStack>
    </>
  );
};
